import { useEffect, useRef, type ReactNode } from 'react';

/** The open modals, outermost first: Escape closes only the topmost layer (#120). */
const openModals: symbol[] = [];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modal (frontend-patterns §9.4): escape closes the topmost one, backdrop click
 *  closes (unless told not to), content click does not. Focus moves into the dialog,
 *  stays inside it while it is open, and returns to where it was on close.
 *  `locked` blocks every way to dismiss it (a request is in flight). */
export function Modal({
  title,
  onClose,
  children,
  wide = false,
  locked = false,
  dismissOnBackdrop = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  locked?: boolean;
  dismissOnBackdrop?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // The latest props, read by the long-lived key handler below.
  const latest = useRef({ onClose, locked });
  latest.current = { onClose, locked };

  useEffect(() => {
    const id = Symbol('modal');
    openModals.push(id);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      (dialog.querySelector<HTMLElement>(FOCUSABLE) ?? dialog).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== id) return; // not the topmost layer
      if (e.key === 'Escape') {
        if (!latest.current.locked) latest.current.onClose();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !dialog.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = openModals.indexOf(id);
      if (at >= 0) openModals.splice(at, 1);
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, []);

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (dismissOnBackdrop && !locked) onClose();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={wide ? 'modal modal-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          {!locked && (
            <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close dialog">
              ✕
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
